import { useEffect, useRef } from 'react';

import { getAuthToken, useAuth } from 'features/auth/hooks/useAuth';

declare global {
  interface Window {
    FleetPandaSwitcher?: {
      init: (config: {
        appIdentifier: string;
        apiUrl: string;
        getToken: () => string | null;
        position?: 'left' | 'right';
        theme?: 'dark' | 'light';
        appGrouping?: boolean;
        autoCollapseMs?: number;
        onLogout?: () => void;
      }) => Promise<void>;
      destroy: () => void;
      refresh: () => Promise<void>;
    };
  }
}

const API_URL = process.env.REACT_APP_REDIRECT_URL || window.location.origin;

// App identifier the switcher backend uses to resolve this app in the registry.
const APP_IDENTIFIER = process.env.REACT_APP_SWITCHER_APP_IDENTIFIER || 'dispatch';

/**
 * Headless component that initializes the Unified App Switcher sidebar.
 *
 * The sidebar is rendered outside React via Shadow DOM by the unified
 * switcher widget IIFE, loaded in index.html from REACT_APP_SWITCHER_URL.
 *
 * This component:
 *   1. Waits for a valid auth token (fp_access_token cookie)
 *   2. Calls FleetPandaSwitcher.init() with dispatch-specific config
 *   3. Cleans up on unmount
 *
 * The API URL points to the Dispatch Rails backend which proxies
 * /switcher/apps and /switcher/switch to the Go Switcher backend
 * via the switcher-client gem.
 */
export const AppSwitcher = () => {
  const initializedRef = useRef(false);
  const { logout } = useAuth();

  // Keep the latest logout callback in a ref so the init effect can run once
  // (on mount) while still invoking the current full-logout-and-redirect impl.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return; // not signed in yet — nothing to initialize

    // Widget bundle absent: REACT_APP_SWITCHER_URL is unset/misconfigured or the
    // bundle failed to load. Non-critical — log it and let the app run without
    // the sidebar rather than breaking the page.
    if (!window.FleetPandaSwitcher) {
      // eslint-disable-next-line no-console
      console.warn(
        '[switcher] sidebar widget unavailable; check REACT_APP_SWITCHER_URL. App continues without it.'
      );
      return;
    }

    // Prevent double-init in StrictMode
    if (initializedRef.current) return;
    initializedRef.current = true;

    window.FleetPandaSwitcher.init({
      appIdentifier: APP_IDENTIFIER,
      apiUrl: API_URL,
      getToken: () => getAuthToken(),
      position: 'left',
      theme: 'dark',
      appGrouping: true,
      autoCollapseMs: 5000,
      onLogout: () => logoutRef.current(),
    }).catch((err) => {
      // Init failure is non-critical — log it; the app continues without sidebar.
      // eslint-disable-next-line no-console
      console.warn('[switcher] sidebar init failed; continuing without it.', err);
    });

    return () => {
      initializedRef.current = false;
      window.FleetPandaSwitcher?.destroy();
    };
  }, []);

  return null;
};
